/**
 * Shared option lists for the Create Policy form (F039 Autonomous Governance), consumed by both
 * the form component and `ResponsePolicyCreate.schema.ts`.
 *
 * SUPERSEDED (Charter M3 migration): the `VP-` validator functions below and
 * `mapServerErrorToFormState` are no longer wired into `ResponsePolicyForm.tsx` — it now validates
 * via the imported `ResponsePolicyCreateSchema` (`react-hook-form` + `zodResolver`) and maps server
 * errors via the shared `src/components/forms/applyServerErrors.ts`. This module is kept,
 * unchanged, so `ResponsePolicyForm.test.tsx`'s exercised behaviour and any direct unit coverage of
 * these functions keeps passing, and because the enum option lists are still the schema's source
 * of truth. See `ResponsePolicyCreate.schema.ts` for the live validators (same rule IDs, same
 * verbatim messages).
 */
import type { ResponseActionType, ResponseImpactClass, ResponseSeverity, ResponseTriggerKind } from '../../../types';

// ---------------------------------------------------------------------------
// Enum option lists (verbatim C# enum member order)
// ---------------------------------------------------------------------------

export const RESPONSE_TRIGGER_KIND_OPTIONS: ResponseTriggerKind[] = [
  'GovernanceDrift',
  'SevereRegeVerdict',
  'RiskPath',
];

export const RESPONSE_SEVERITY_OPTIONS: ResponseSeverity[] = ['Low', 'Medium', 'High', 'Critical'];

export const RESPONSE_ACTION_TYPE_OPTIONS: ResponseActionType[] = [
  'RevokePermissions',
  'ReduceExecutionScope',
  'DowngradeToReadOnly',
  'DisableAutomation',
  'IsolateConnector',
  'InsertApprovalGate',
  'TerminateExecutionChain',
  'ForceSandboxMode',
];

export const RESPONSE_IMPACT_CLASS_OPTIONS: ResponseImpactClass[] = ['Low', 'Medium', 'High'];

// ---------------------------------------------------------------------------
// Validation Matrix — Create Policy (`VP-`) — one function = one rule.
// ---------------------------------------------------------------------------

/** VP-01 — External Id, required @ submit. */
export function externalIdRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'External id is required';
}
/** VP-02 — External Id, max 200 chars @ blur. INFERRED provenance — matches the Register Server
 *  form's identifier bound for consistency. */
export function externalIdMaxLengthError(value: string): string | undefined {
  return value.length > 200 ? 'External id must be 200 characters or fewer' : undefined;
}

/** VP-03 — Name, required @ submit. */
export function policyNameRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Name is required';
}
/** VP-04 — Name, max 200 chars @ blur. */
export function policyNameMaxLengthError(value: string): string | undefined {
  return value.length > 200 ? 'Name must be 200 characters or fewer' : undefined;
}

/** VP-05 — Description, max 2000 chars @ blur (optional field). */
export function descriptionMaxLengthError(value: string): string | undefined {
  return value.length > 2000 ? 'Description must be 2000 characters or fewer' : undefined;
}

/** VP-06 — Applies To Trigger, one of ResponseTriggerKind @ submit. */
export function appliesToTriggerRequiredError(value: string): string | undefined {
  return value ? undefined : 'Select which trigger this policy applies to';
}

/** VP-07 — Min Severity, one of ResponseSeverity @ submit. */
export function minSeverityRequiredError(value: string): string | undefined {
  return value ? undefined : 'Select a minimum severity';
}

/** VP-08 — Condition Expression, required @ submit. */
export function conditionExpressionRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Condition expression is required';
}
/** VP-09 — Condition Expression, max 2000 chars @ blur. INFERRED provenance. */
export function conditionExpressionMaxLengthError(value: string): string | undefined {
  return value.length > 2000 ? 'Condition expression must be 2000 characters or fewer' : undefined;
}

/** VP-10 — Action, one of ResponseActionType @ submit. */
export function actionRequiredError(value: string): string | undefined {
  return value ? undefined : 'Select a response action';
}

/** VP-11 — Priority, required + a whole number @ submit. */
export function priorityRequiredError(value: string): string | undefined {
  if (!value.trim()) return 'Priority is required';
  if (!/^-?\d+$/.test(value.trim())) return 'Priority must be a whole number';
  return undefined;
}

/** VP-12 — Debounce Window, valid `hh:mm:ss` .NET TimeSpan format when non-empty @ blur (optional
 *  field). */
export function debounceWindowFormatError(value: string): string | undefined {
  if (!value.trim()) return undefined;
  return /^\d{2}:\d{2}:\d{2}$/.test(value.trim())
    ? undefined
    : 'Debounce window must be in hh:mm:ss format, e.g. 00:05:00';
}

// ---------------------------------------------------------------------------
// Server-error mapping — LOCAL COPY of the F031/F032 helper of the same name.
// ---------------------------------------------------------------------------

export type FieldErrorMap = Record<string, string>;

interface ServerErrorShape {
  message?: string;
  details?: Record<string, string[] | string>;
}

export function mapServerErrorToFormState(
  error: unknown,
  knownFields: readonly string[]
): { fieldErrors: FieldErrorMap; globalMessage?: string } {
  const fieldErrors: FieldErrorMap = {};
  let globalMessage: string | undefined;

  const shaped = error as ServerErrorShape;
  const details = shaped?.details;
  if (details && typeof details === 'object') {
    for (const [key, val] of Object.entries(details)) {
      // Newtonsoft's default PascalCase output (`Name`) -> camelCase field key (`name`).
      const camelKey = key.length > 0 ? key.charAt(0).toLowerCase() + key.slice(1) : key;
      const message = Array.isArray(val) ? val.join(' ') : String(val);
      if (knownFields.includes(camelKey)) {
        fieldErrors[camelKey] = message;
      } else {
        globalMessage = globalMessage ? `${globalMessage} ${message}` : message;
      }
    }
  }

  if (Object.keys(fieldErrors).length === 0 && !globalMessage) {
    globalMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
  }

  return { fieldErrors, globalMessage };
}
